import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    'project-type': 'Genome-Resolved Analytics',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Obfuscate email address - gets assembled after page load to avoid bot detection
    const emailElement = document.getElementById('obfuscated-email');
    if (emailElement) {
      const parts = ['info', '@', 'ocean', 'bio', 'metrics', '.', 'com'];
      emailElement.textContent = parts.join('');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // EmailJS configuration - Replace these with your actual values
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

      // Template parameters that match your EmailJS template
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company,
        project_type: formData['project-type'],
        message: formData.message,
        current_date: new Date().toLocaleString(),
        to_email: 'florence@oceanbiometrics.com'
      };

      console.log('Sending email with params:', templateParams);

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setSubmitStatus('success');
      // Reset form on success
      setFormData({
        name: '',
        company: '',
        email: '',
        'project-type': 'Genome-Resolved Analytics',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-5" style={{
      backgroundImage: `linear-gradient(rgba(10, 14, 20, 0.85), rgba(10, 14, 20, 0.85)), url('/images/background-images/fish.jpg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      position: 'relative'
    }}>
      <div className="container">
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8 text-center">
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              marginBottom: '1rem',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              color: '#EFEFE3',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              Get in Touch
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#D5D6CC',
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              Let's discuss how Ocean BioMetrics can accelerate your marine impact goals.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-8 mx-auto">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="name" className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="company" className="form-label">Company</label>
                  <input
                    type="text"
                    className="form-control"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="project-type" className="form-label">Primary Interest</label>
                  <select
                    className="form-control"
                    id="project-type"
                    name="project-type"
                    value={formData['project-type']}
                    onChange={handleInputChange}
                  >
                    <option>Genome-Resolved Analytics</option>
                    <option>Knowledge-Graph Intelligence</option>
                    <option>Ecological/Metabolic Networks & Time-Series Analyses</option>
                    <option>Multiple Areas</option>
                    <option>Not Sure Yet</option>
                  </select>
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="message" className="form-label">Message</label>
                <textarea
                  className="form-control"
                  id="message"
                  name="message"
                  rows={5}
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                ></textarea>
              </div>
              {submitStatus === 'success' && (
                <div className="alert alert-success mb-4" style={{
                  backgroundColor: 'rgba(24, 195, 186, 0.1)',
                  border: '1px solid rgba(24, 195, 186, 0.3)',
                  color: '#18C3BA'
                }}>
                  ✓ Message sent successfully! We'll get back to you soon.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="alert alert-danger mb-4" style={{
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  color: '#dc3545'
                }}>
                  ✗ Failed to send message. Please try again or contact us directly.
                </div>
              )}
              <div className="text-center" style={{ marginTop: '2.5rem' }}>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '1rem 2.5rem',
                    background: 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(24, 195, 186, 0.3)',
                    borderRadius: '50px',
                    color: '#18C3BA',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    fontFamily: "'Montserrat', sans-serif",
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 8px 25px rgba(24, 195, 186, 0.15)',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.25) 0%, rgba(24, 195, 186, 0.35) 100%)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 12px 35px rgba(24, 195, 186, 0.25)';
                      e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(24, 195, 186, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.3)';
                    }
                  }}
                >
                  <span>{isSubmitting ? 'Sending...' : 'Get in Touch'}</span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <path
                      d="M7 17L17 7M17 7H7M17 7V17"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;